"use client"

import { Button } from "@repo/ui/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui/components/ui/tooltip"
import { H2 } from "@repo/ui/components/ui/typography"
import { Printer } from "lucide-react"
import { ReactNode, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslations } from "use-intl"

const PRINTING_CLASS = "julo-printing"

interface Props {
  /** Heading shown at the top of the printed page. */
  title: string
  /** What gets printed. Rendered only while printing. */
  children: ReactNode
  className?: string
}

/**
 * Prints `children` on their own. While printing, they are rendered into a
 * container directly under <body> and everything else is hidden (see
 * `.julo-print-root` in globals.css), so it works from inside dialogs too.
 */
export const PrintReceiptButton = ({ title, children, className }: Props) => {
  const t = useTranslations("ui.printReceiptButton")
  const [isPrinting, setIsPrinting] = useState(false)

  useEffect(() => {
    if (!isPrinting) return

    const root = document.documentElement
    const handleAfterPrint = () => setIsPrinting(false)

    root.classList.add(PRINTING_CLASS)
    window.addEventListener("afterprint", handleAfterPrint)
    window.print()

    return () => {
      root.classList.remove(PRINTING_CLASS)
      window.removeEventListener("afterprint", handleAfterPrint)
    }
  }, [isPrinting])

  const handlePrint = () => setIsPrinting(true)

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={className}
              onClick={handlePrint}
              aria-label={t("print")}
            >
              <Printer className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("print")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {isPrinting &&
        createPortal(
          <div className="julo-print-root">
            <H2 size="xl" weight="semibold" className="mb-4">
              {title}
            </H2>
            {children}
          </div>,
          document.body,
        )}
    </>
  )
}
